import { InfoOutlined as InfoOutlinedIcon } from "@mui/icons-material";
import { Autocomplete, Stack, TextField, Tooltip } from "@mui/material";
import { getTranslation } from "common";
import { useTranslations } from "hooks";
import {
  SLA_EDITOR_TEXT_KEYS,
  SLA_OWNER_CATEGORY_LABEL_KEYS,
  type SlaEditorActorOption,
  type SlaOwnerOption,
  type SlaTargetDraft,
} from "model/slaEditorModel";
import { useMemo } from "react";
import { slaOwnerOptionsForDraft } from "./useSlaOwnerOptions";

/**
 * Responsabile dello SLA: solo utente (2026-10-08, il ruolo responsabile e'
 * stato rimosso dal designer e si invia NULL).
 *
 * Utenti ammessi (`useSlaOwnerOptions`): amministratori dell'organizzazione,
 * capi reparto e partecipanti del processo, raggruppati per categoria. Il
 * responsabile gia' salvato che non e' piu' ammesso resta visibile nel gruppo
 * `unavailable` con un avviso sul campo, invece di sparire in silenzio.
 */
interface SlaResponsibleFieldsProps {
  draft: SlaTargetDraft;
  onChange: (patch: Partial<SlaTargetDraft>) => void;
  options: SlaOwnerOption[];
  /** Tutti gli utenti dell'organizzazione: nome del responsabile non ammesso. */
  knownUsers: SlaEditorActorOption[];
  loading?: boolean;
  disabled?: boolean;
  idPrefix: string;
}

const SlaResponsibleFields = ({
  draft,
  onChange,
  options,
  knownUsers,
  loading = false,
  disabled = false,
  idPrefix,
}: SlaResponsibleFieldsProps) => {
  const translations = useTranslations();
  const t = (key: string) => getTranslation(translations, key);

  const draftOptions = useMemo(
    () => slaOwnerOptionsForDraft(options, draft.responsibleUserId, knownUsers),
    [options, draft.responsibleUserId, knownUsers],
  );
  const value =
    draftOptions.find((option) => option.id === draft.responsibleUserId) ?? null;
  // Durante il caricamento il responsabile salvato non e' ancora
  // classificabile: resta visibile senza essere segnalato.
  const notEligible = !loading && value?.category === "unavailable";

  return (
    <Stack direction="row" spacing={1} alignItems="flex-start">
      <Autocomplete<SlaOwnerOption, false, false, false>
        id={`${idPrefix}-responsible-user`}
        size="small"
        fullWidth
        disabled={disabled}
        loading={loading}
        options={draftOptions}
        value={value}
        groupBy={(option) => t(SLA_OWNER_CATEGORY_LABEL_KEYS[option.category])}
        getOptionLabel={(option) => option.name}
        isOptionEqualToValue={(option, selected) => option.id === selected.id}
        noOptionsText={t(SLA_EDITOR_TEXT_KEYS.responsibleNoOptions)}
        loadingText={t(SLA_EDITOR_TEXT_KEYS.responsibleLoading)}
        onChange={(_, selected) => onChange({ responsibleUserId: selected?.id ?? null })}
        renderInput={(params) => (
          <TextField
            {...params}
            label={t(SLA_EDITOR_TEXT_KEYS.responsibleUser)}
            placeholder={t(SLA_EDITOR_TEXT_KEYS.responsibleNone)}
            helperText={notEligible ? t(SLA_EDITOR_TEXT_KEYS.responsibleNotEligible) : undefined}
            FormHelperTextProps={
              notEligible ? { sx: { color: "warning.main" } } : undefined
            }
          />
        )}
      />
      <Tooltip title={t(SLA_EDITOR_TEXT_KEYS.responsibleUserInfo)} placement="top" arrow>
        <InfoOutlinedIcon
          fontSize="small"
          color="action"
          tabIndex={0}
          aria-label={t(SLA_EDITOR_TEXT_KEYS.responsibleUserInfo)}
          data-testid={`${idPrefix}-responsible-user-info`}
          sx={{ mt: 1, cursor: "help", flexShrink: 0 }}
        />
      </Tooltip>
    </Stack>
  );
};

export default SlaResponsibleFields;
